import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFile,
    faGripVertical,
    faLock,
    faPen,
    faPlus,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { Input, ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../Rodal/Rodal';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import './EditRequiredDocumentTypesModal.scss';

const DOCUMENT_TYPE_MAX_LENGTH = 200;

interface EditRequiredDocumentTypesModalProps {
    isOpen: boolean;
    requiredDocumentTypes: string[];
    documentTypesWithUploads: string[];
    commonDocumentTypes: string[];
    isConfirmingSave: boolean;
    canAddCommonDocumentTypes: boolean;
    onClose: () => void;
    onSave: (documentTypes: string[]) => Promise<boolean>;
    onAddCommonDocumentType: (name: string) => Promise<boolean>;
    canChangeCommonDocumentTypes: boolean;
    onRenameCommonDocumentType: (name: string, newName: string) => Promise<boolean>;
    onRemoveCommonDocumentType: (name: string) => Promise<boolean>;
}

const isSameDocumentType = (first: string, second: string) =>
    first.toLowerCase() === second.toLowerCase();

export const EditRequiredDocumentTypesModal = (
    props: EditRequiredDocumentTypesModalProps
) => {
    const [documentTypes, setDocumentTypes] = useState<string[]>([]);
    const [newDocumentType, setNewDocumentType] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isAddingCommonType, setIsAddingCommonType] = useState(false);
    const [renamingCommonType, setRenamingCommonType] = useState<string>();
    const [commonTypeNewName, setCommonTypeNewName] = useState('');
    const [removingCommonType, setRemovingCommonType] = useState<string>();
    const [isChangingCommonType, setIsChangingCommonType] = useState(false);

    useEffect(() => {
        if (props.isOpen) {
            setDocumentTypes(props.requiredDocumentTypes);
            setNewDocumentType('');
            setErrorMessage('');
            setRenamingCommonType(undefined);
            setRemovingCommonType(undefined);
        }
    }, [props.isOpen]);

    const isInList = (documentType: string) =>
        documentTypes.some((existing) => isSameDocumentType(existing, documentType));

    const addDocumentType = (documentType: string) => {
        const name = documentType.trim();
        if (!name) {
            return false;
        }
        if (isInList(name)) {
            setErrorMessage(`"${name}" is already in the list.`);
            return false;
        }
        setDocumentTypes([...documentTypes, name]);
        setErrorMessage('');
        return true;
    };

    const addTypedDocumentType = () => {
        if (addDocumentType(newDocumentType)) {
            setNewDocumentType('');
        }
    };

    const addTypedToCommonTypes = async () => {
        const name = newDocumentType.trim();
        if (
            props.commonDocumentTypes.some((existing) =>
                isSameDocumentType(existing, name)
            )
        ) {
            setErrorMessage(`"${name}" is already a common document type.`);
            return;
        }
        setIsAddingCommonType(true);
        const succeeded = await props.onAddCommonDocumentType(name);
        setIsAddingCommonType(false);
        if (succeeded) {
            setNewDocumentType('');
            setErrorMessage('');
        }
    };

    const startRenamingCommonType = (documentType: string) => {
        setRemovingCommonType(undefined);
        setRenamingCommonType(documentType);
        setCommonTypeNewName(documentType);
        setErrorMessage('');
    };

    const renameCommonType = async () => {
        const newName = commonTypeNewName.trim();
        if (!newName || isChangingCommonType) {
            return;
        }
        if (newName === renamingCommonType) {
            setRenamingCommonType(undefined);
            return;
        }
        if (
            props.commonDocumentTypes.some(
                (existing) =>
                    existing !== renamingCommonType &&
                    isSameDocumentType(existing, newName)
            )
        ) {
            setErrorMessage(`"${newName}" is already a common document type.`);
            return;
        }
        setIsChangingCommonType(true);
        const succeeded = await props.onRenameCommonDocumentType(
            renamingCommonType,
            newName
        );
        setIsChangingCommonType(false);
        if (succeeded) {
            setRenamingCommonType(undefined);
            setErrorMessage('');
        }
    };

    const removeCommonType = async () => {
        setIsChangingCommonType(true);
        const succeeded = await props.onRemoveCommonDocumentType(removingCommonType);
        setIsChangingCommonType(false);
        if (succeeded) {
            setRemovingCommonType(undefined);
        }
    };

    const removeDocumentType = (documentType: string) =>
        setDocumentTypes(documentTypes.filter((existing) => existing !== documentType));

    const dropCommonDocumentType = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        addDocumentType(e.dataTransfer.getData('text/plain'));
    };

    const save = async () => {
        const typedName = newDocumentType.trim();
        if (typedName && isInList(typedName)) {
            setErrorMessage(`"${typedName}" is already in the list.`);
            return;
        }
        const documentTypesToSave = typedName ? [...documentTypes, typedName] : documentTypes;
        setIsSaving(true);
        const succeeded = await props.onSave(documentTypesToSave);
        setIsSaving(false);
        if (succeeded) {
            props.onClose();
        }
    };

    const availableCommonDocumentTypes = props.commonDocumentTypes.filter(
        (documentType) => !isInList(documentType)
    );
    const hasTypedName = newDocumentType.trim().length > 0;

    const renderCommonChip = (documentType: string, isListed = false) => (
        <button
            type="button"
            draggable={!isListed}
            disabled={isListed}
            title={isListed ? 'Already in the list' : undefined}
            className="edit-required-document-types__common-chip"
            key={documentType}
            onDragStart={(e) => e.dataTransfer.setData('text/plain', documentType)}
            onClick={() => addDocumentType(documentType)}>
            <FontAwesomeIcon
                icon={faGripVertical}
                className="edit-required-document-types__grip"
            />
            {documentType}
            <FontAwesomeIcon
                icon={faPlus}
                className="edit-required-document-types__chip-plus"
            />
        </button>
    );

    const renderManagedCommonType = (documentType: string) => {
        if (renamingCommonType === documentType) {
            return (
                <div
                    className="edit-required-document-types__common-manage"
                    key={documentType}>
                    <Input
                        type="text"
                        value={commonTypeNewName}
                        maxLength={DOCUMENT_TYPE_MAX_LENGTH}
                        aria-label={`New name for ${documentType}`}
                        onChange={(e: any) => {
                            setCommonTypeNewName(e.target.value);
                            setErrorMessage('');
                        }}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                renameCommonType();
                            }
                        }}
                    />
                    <ButtonLoader
                        className="btn btn-primary btn-sm"
                        buttonText="Rename"
                        loaderButtonText="Rename"
                        isLoading={isChangingCommonType}
                        disabled={!commonTypeNewName.trim()}
                        onClick={renameCommonType}
                    />
                    <button
                        type="button"
                        className="btn btn-cancel btn-sm"
                        onClick={() => setRenamingCommonType(undefined)}>
                        Cancel
                    </button>
                </div>
            );
        }

        if (removingCommonType === documentType) {
            return (
                <div
                    className="edit-required-document-types__common-manage"
                    key={documentType}>
                    <span className="edit-required-document-types__common-manage-text">
                        Remove &quot;{documentType}&quot; from the common types on
                        every engagement?
                    </span>
                    <ButtonLoader
                        className="btn btn-danger btn-sm"
                        buttonText="Remove"
                        loaderButtonText="Remove"
                        isLoading={isChangingCommonType}
                        disabled={false}
                        onClick={removeCommonType}
                    />
                    <button
                        type="button"
                        className="btn btn-cancel btn-sm"
                        onClick={() => setRemovingCommonType(undefined)}>
                        Keep
                    </button>
                </div>
            );
        }

        return (
            <div
                className="edit-required-document-types__common-chip-group"
                key={documentType}>
                {renderCommonChip(documentType, isInList(documentType))}
                <button
                    type="button"
                    className="edit-required-document-types__common-action"
                    aria-label={`Rename ${documentType}`}
                    title="Rename"
                    onClick={() => startRenamingCommonType(documentType)}>
                    <FontAwesomeIcon icon={faPen} />
                </button>
                <button
                    type="button"
                    className="edit-required-document-types__common-action"
                    aria-label={`Remove ${documentType} from common types`}
                    title="Remove from common types"
                    onClick={() => {
                        setRenamingCommonType(undefined);
                        setRemovingCommonType(documentType);
                        setErrorMessage('');
                    }}>
                    <FontAwesomeIcon icon={faTrashCan} />
                </button>
            </div>
        );
    };

    const renderAddDocumentType = () => (
        <>
            <div className="edit-required-document-types__add">
                <Input
                    type="text"
                    value={newDocumentType}
                    maxLength={DOCUMENT_TYPE_MAX_LENGTH}
                    placeholder="Add a document, e.g. Bill of Sale"
                    onChange={(e: any) => {
                        setNewDocumentType(e.target.value);
                        setErrorMessage('');
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addTypedDocumentType();
                        }
                    }}
                />
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={!hasTypedName}
                    onClick={addTypedDocumentType}>
                    <FontAwesomeIcon icon={faPlus} />{' '}
                    {props.canAddCommonDocumentTypes ? 'Add to List' : 'Add'}
                </button>
                {props.canAddCommonDocumentTypes && (
                    <ButtonLoader
                        className="btn btn-outline-primary"
                        buttonText="Add to Common Types"
                        loaderButtonText="Add to Common Types"
                        isLoading={isAddingCommonType}
                        disabled={!hasTypedName}
                        onClick={addTypedToCommonTypes}
                    />
                )}
            </div>
            {errorMessage ? (
                <p className="edit-required-document-types__error">
                    {errorMessage}
                </p>
            ) : (
                <p className="edit-required-document-types__hint">
                    {props.canAddCommonDocumentTypes
                        ? 'Common types are available on every engagement.'
                        : 'New document types are added to the current list for this deal only.'}
                </p>
            )}
        </>
    );

    return (
        <Rodal
            visible={props.isOpen}
            onClose={props.onClose}
            animation="fade"
            showMask={true}
            width={560}
            showCloseButton={true}
            className="edit-required-document-types-modal">
            <ModalHeader>Document Types Required to Close</ModalHeader>
            <ModalBody>
                <div className="edit-required-document-types__content">
                    <p className="edit-required-document-types__intro">
                        List every document needed to close this deal. Each one
                        gets its own drafts and final version on this tab.
                    </p>
                    <ul
                        className={
                            isDragOver
                                ? 'edit-required-document-types__list edit-required-document-types__list--drag-over'
                                : 'edit-required-document-types__list'
                        }
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOver(true);
                        }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={dropCommonDocumentType}>
                        {documentTypes.map((documentType) => (
                            <li
                                className="edit-required-document-types__item"
                                key={documentType}>
                                <FontAwesomeIcon
                                    icon={faFile}
                                    className="edit-required-document-types__item-icon"
                                />
                                <span className="edit-required-document-types__item-name">
                                    {documentType}
                                </span>
                                {props.documentTypesWithUploads.includes(
                                    documentType
                                ) ? (
                                    <span className="edit-required-document-types__locked">
                                        <FontAwesomeIcon icon={faLock} /> Has
                                        uploads
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="edit-required-document-types__remove"
                                        aria-label={`Remove ${documentType}`}
                                        onClick={() =>
                                            removeDocumentType(documentType)
                                        }>
                                        <FontAwesomeIcon icon={faTrashCan} />{' '}
                                        Remove
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                    {!props.canAddCommonDocumentTypes &&
                        renderAddDocumentType()}
                    <h5 className="edit-required-document-types__common-title">
                        COMMON DOCUMENT TYPES
                    </h5>
                    <p className="edit-required-document-types__hint">
                        Drag into the list above, or click to add.
                    </p>
                    <div className="edit-required-document-types__common-chips">
                        {props.canChangeCommonDocumentTypes
                            ? props.commonDocumentTypes.map(renderManagedCommonType)
                            : availableCommonDocumentTypes.map((documentType) =>
                                  renderCommonChip(documentType)
                              )}
                    </div>
                    {props.canAddCommonDocumentTypes && renderAddDocumentType()}
                </div>
                <div className="buttons-wrapper">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={props.onClose}>
                        Cancel
                    </button>
                    <ButtonLoader
                        className="btn btn-primary"
                        buttonText={
                            props.isConfirmingSave
                                ? 'Save & Confirm List'
                                : 'Save'
                        }
                        loaderButtonText={
                            props.isConfirmingSave
                                ? 'Save & Confirm List'
                                : 'Save'
                        }
                        isLoading={isSaving}
                        disabled={documentTypes.length === 0 && !hasTypedName}
                        onClick={save}
                    />
                </div>
            </ModalBody>
        </Rodal>
    );
};
